/**
 * DesignDocsPanel — sidebar surface for browsing the per-game design
 * specs.
 *
 * Layout:
 *   ┌─ Toolbar ──────────────────────────────────────┐
 *   │ [search]  [+ New spec]   [reset]  [stats]     │
 *   ├─ Category tabs ────────────────────────────────┤
 *   │  All • Overview • Mechanics • System Contracts │
 *   │  Decisions • Open Questions                    │
 *   ├─ Table (sortable columns) ─────────────────────┤
 *   │  ID  Title  System  Bindings  Updated  Status  │
 *   │  ────────────────────────────────────────────  │
 *   │  spec_…  Snake speed  …  2  3m ago  active    │
 *   │  …                                             │
 *   ├─ Detail (when row clicked) ────────────────────┤
 *   │  rendered markdown body + linked specs/symbols │
 *   └────────────────────────────────────────────────┘
 *
 * This is read-mostly today — clicking a row shows the spec; archiving
 * is in the detail pane. Editing is via the agent's tools (which write
 * to the same store via the agent → sandbox bridge once that's
 * wired up). For now there's a manual `New spec` button so designers
 * can capture facts directly.
 */

import { useMemo, useState } from 'react';
import {
  CATEGORY_LABEL,
  CATEGORY_BLURB,
  DESIGN_SPEC_CATEGORIES,
  type DesignSpec,
  type DesignSpecCategory,
} from './types';
import {
  createSpec,
  deleteSpec,
  resetToSeed,
  updateSpec,
  useDesignSpecs,
} from './designStore';
import { CoverageSection } from './CoverageSection';
import { parseCoverageFromFiles } from './coverageStore';
import './DesignDocsPanel.css';

// ─────────────────────────────────────────────────────────────────────
// Filter / Sort State
// ─────────────────────────────────────────────────────────────────────

type CategoryFilter = 'all' | DesignSpecCategory;

type SortColumn = 'title' | 'category' | 'system' | 'bindings' | 'updated';
type SortDir = 'asc' | 'desc';

interface SortState {
  column: SortColumn;
  dir: SortDir;
}

const DEFAULT_SORT: SortState = { column: 'updated', dir: 'desc' };

type ViewMode = 'specs' | 'coverage';

// ─────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────

export interface DesignDocsPanelProps {
  /**
   * Optional source for the Coverage view — when provided, a
   * "Coverage" mode tab appears alongside "Specs". The function is
   * called on each render to fetch the latest sandbox files map
   * (containing the runtime's persistence files under
   * `/game-design/.reconciler/`). Omit to hide the Coverage tab.
   */
  getCoverageFiles?: () => Map<string, string>;
}

export function DesignDocsPanel({ getCoverageFiles }: DesignDocsPanelProps = {}) {
  const specs = useDesignSpecs();

  const [viewMode, setViewMode] = useState<ViewMode>('specs');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const coverageData = useMemo(
    () => getCoverageFiles ? parseCoverageFromFiles(getCoverageFiles()) : null,
    // We intentionally re-derive on every render. parseCoverageFromFiles
    // is fast (low-thousand entries max) and the source map mutates via
    // identity changes from the parent.
    [getCoverageFiles],
  );

  const visible = useMemo(() => filterAndSort(specs, {
    category: categoryFilter,
    search: search.trim().toLowerCase(),
    showArchived,
    sort,
  }), [specs, categoryFilter, search, showArchived, sort]);

  const counts = useMemo(() => countByCategory(specs, showArchived), [specs, showArchived]);
  const selected = selectedId ? specs.find(s => s.id === selectedId) ?? null : null;

  const handleNewSpec = () => {
    const spec = createSpec({
      title: 'Untitled spec',
      category: categoryFilter === 'all' ? 'mechanic' : categoryFilter,
      body: '(describe one fact here)',
      relatedCodeSymbols: [],
      relatedSpecIds: [],
    });
    setSelectedId(spec.id);
  };

  const handleSort = (column: SortColumn) => {
    setSort(prev => prev.column === column
      ? { column, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { column, dir: column === 'updated' ? 'desc' : 'asc' });
  };

  return (
    <div className="design-docs-panel" data-testid="design-docs-panel">
      {coverageData && (
        <div className="dd-mode-tabs" role="tablist" aria-label="Design panel view">
          <button
            role="tab"
            aria-selected={viewMode === 'specs'}
            className={`dd-mode-tab ${viewMode === 'specs' ? 'dd-mode-tab-active' : ''}`}
            onClick={() => setViewMode('specs')}
          >
            Specs
          </button>
          <button
            role="tab"
            aria-selected={viewMode === 'coverage'}
            className={`dd-mode-tab ${viewMode === 'coverage' ? 'dd-mode-tab-active' : ''}`}
            onClick={() => setViewMode('coverage')}
            data-testid="dd-mode-coverage"
          >
            Coverage
            {coverageData.reviewQueue.length > 0 && (
              <span className="dd-mode-tab-badge">{coverageData.reviewQueue.length}</span>
            )}
          </button>
        </div>
      )}

      {viewMode === 'coverage' && coverageData && (
        <CoverageSection data={coverageData} />
      )}

      {viewMode === 'specs' && <>
      <Toolbar
        search={search}
        onSearchChange={setSearch}
        showArchived={showArchived}
        onToggleArchived={() => setShowArchived(v => !v)}
        onNewSpec={handleNewSpec}
        onResetSeed={() => {
          if (confirm('Reset all design specs to the seeded examples? Your custom specs will be deleted.')) {
            resetToSeed();
            setSelectedId(null);
          }
        }}
      />

      <CategoryTabs
        active={categoryFilter}
        counts={counts}
        onChange={setCategoryFilter}
      />

      {visible.length === 0 ? (
        <EmptyState category={categoryFilter} />
      ) : (
        <SpecTable
          specs={visible}
          sort={sort}
          onSort={handleSort}
          selectedId={selectedId}
          onSelect={setSelectedId}
        />
      )}

      {selected && (
        <SpecDetail
          spec={selected}
          allSpecs={specs}
          onClose={() => setSelectedId(null)}
          onPatch={(patch) => updateSpec(selected.id, patch)}
          onDelete={() => {
            if (confirm(`Delete spec ${selected.id}? This cannot be undone (use archive instead to preserve audit trail).`)) {
              deleteSpec(selected.id);
              setSelectedId(null);
            }
          }}
        />
      )}
      </>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Toolbar
// ─────────────────────────────────────────────────────────────────────

interface ToolbarProps {
  search: string;
  onSearchChange: (v: string) => void;
  showArchived: boolean;
  onToggleArchived: () => void;
  onNewSpec: () => void;
  onResetSeed: () => void;
}

function Toolbar({ search, onSearchChange, showArchived, onToggleArchived, onNewSpec, onResetSeed }: ToolbarProps) {
  return (
    <div className="dd-toolbar">
      <input
        type="text"
        className="dd-search"
        placeholder="Search specs…"
        value={search}
        onChange={e => onSearchChange(e.target.value)}
      />
      <button
        className={`dd-toolbar-btn ${showArchived ? 'active' : ''}`}
        title={showArchived ? 'Hide archived specs' : 'Show archived specs'}
        onClick={onToggleArchived}
      >
        {showArchived ? '✓ Archived' : 'Archived'}
      </button>
      <button className="dd-toolbar-btn primary" onClick={onNewSpec} title="Create a new spec">
        + New
      </button>
      <button className="dd-toolbar-btn" onClick={onResetSeed} title="Reset to seeded examples">
        Reset
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Category tabs
// ─────────────────────────────────────────────────────────────────────

interface CategoryTabsProps {
  active: CategoryFilter;
  counts: Record<CategoryFilter, number>;
  onChange: (c: CategoryFilter) => void;
}

function CategoryTabs({ active, counts, onChange }: CategoryTabsProps) {
  return (
    <div className="dd-category-tabs" role="tablist">
      <CategoryTab label="All" filter="all" active={active} count={counts.all} onClick={onChange} />
      {DESIGN_SPEC_CATEGORIES.map(cat => (
        <CategoryTab
          key={cat}
          label={CATEGORY_LABEL[cat]}
          filter={cat}
          active={active}
          count={counts[cat]}
          onClick={onChange}
        />
      ))}
    </div>
  );
}

interface CategoryTabProps {
  label: string;
  filter: CategoryFilter;
  active: CategoryFilter;
  count: number;
  onClick: (c: CategoryFilter) => void;
}

function CategoryTab({ label, filter, active, count, onClick }: CategoryTabProps) {
  return (
    <button
      className={`dd-category-tab ${active === filter ? 'active' : ''}`}
      role="tab"
      aria-selected={active === filter}
      onClick={() => onClick(filter)}
    >
      <span className="dd-category-label">{label}</span>
      <span className="dd-category-count">{count}</span>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Table
// ─────────────────────────────────────────────────────────────────────

interface SpecTableProps {
  specs: DesignSpec[];
  sort: SortState;
  onSort: (col: SortColumn) => void;
  selectedId: string | null;
  onSelect: (id: string) => void;
}

function SpecTable({ specs, sort, onSort, selectedId, onSelect }: SpecTableProps) {
  return (
    <div className="dd-table-wrap">
      <table className="dd-table">
        <thead>
          <tr>
            <th onClick={() => onSort('title')} className={sortClass(sort, 'title')}>Title</th>
            <th onClick={() => onSort('category')} className={sortClass(sort, 'category')}>Category</th>
            <th onClick={() => onSort('system')} className={sortClass(sort, 'system')}>System</th>
            <th onClick={() => onSort('bindings')} className={`dd-num ${sortClass(sort, 'bindings')}`} title="Bound code symbols">Code</th>
            <th onClick={() => onSort('updated')} className={sortClass(sort, 'updated')}>Updated</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {specs.map(spec => {
            const archived = !!spec.supersededBy;
            return (
              <tr
                key={spec.id}
                className={`${selectedId === spec.id ? 'selected' : ''} ${archived ? 'archived' : ''}`}
                onClick={() => onSelect(spec.id)}
              >
                <td>
                  <div className="dd-cell-title">
                    <span className="dd-title-text">{spec.title}</span>
                    <span className="dd-cell-id">{spec.id}</span>
                  </div>
                </td>
                <td>
                  <span className={`dd-cat-pill cat-${spec.category}`}>
                    {CATEGORY_LABEL[spec.category]}
                  </span>
                </td>
                <td>{spec.systemName ?? <span className="dd-muted">—</span>}</td>
                <td className="dd-num">{spec.relatedCodeSymbols.length}</td>
                <td title={spec.updatedAt}>{relativeTime(spec.updatedAt)}</td>
                <td>{archived
                  ? <span className="dd-status archived">archived</span>
                  : <span className="dd-status active">active</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function sortClass(sort: SortState, col: SortColumn): string {
  if (sort.column !== col) return 'sortable';
  return `sortable sorted-${sort.dir}`;
}

// ─────────────────────────────────────────────────────────────────────
// Empty state
// ─────────────────────────────────────────────────────────────────────

function EmptyState({ category }: { category: CategoryFilter }) {
  if (category === 'all') {
    return (
      <div className="dd-empty">
        <h3>No design specs</h3>
        <p>
          Specs capture per-game design facts — one fact per spec, each with
          its own ID and code-symbol bindings. Click <strong>+ New</strong> to
          add one, or run the design agent to populate this from existing code.
        </p>
      </div>
    );
  }
  return (
    <div className="dd-empty">
      <h3>No {CATEGORY_LABEL[category]} yet</h3>
      <p>{CATEGORY_BLURB[category]}</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Detail
// ─────────────────────────────────────────────────────────────────────

interface SpecDetailProps {
  spec: DesignSpec;
  allSpecs: DesignSpec[];
  onClose: () => void;
  onPatch: (patch: Partial<DesignSpec>) => void;
  onDelete: () => void;
}

function SpecDetail({ spec, allSpecs, onClose, onPatch, onDelete }: SpecDetailProps) {
  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState(spec.title);
  const [draftBody, setDraftBody] = useState(spec.body);
  const [draftSystem, setDraftSystem] = useState(spec.systemName ?? '');

  // Reset draft when switching specs.
  if (editing && draftTitle === '' && spec.title !== '') {
    setDraftTitle(spec.title);
    setDraftBody(spec.body);
    setDraftSystem(spec.systemName ?? '');
  }

  const startEdit = () => {
    setDraftTitle(spec.title);
    setDraftBody(spec.body);
    setDraftSystem(spec.systemName ?? '');
    setEditing(true);
  };

  const save = () => {
    onPatch({
      title: draftTitle.trim() || spec.title,
      body: draftBody.trim(),
      systemName: draftSystem.trim() || undefined,
    });
    setEditing(false);
  };

  const cancel = () => setEditing(false);

  const linkedSpecs = spec.relatedSpecIds
    .map(id => allSpecs.find(s => s.id === id))
    .filter((s): s is DesignSpec => !!s);

  return (
    <div className="dd-detail">
      <div className="dd-detail-header">
        <div className="dd-detail-title-row">
          {editing
            ? <input
                className="dd-detail-title-input"
                value={draftTitle}
                onChange={e => setDraftTitle(e.target.value)}
                autoFocus
              />
            : <h3 className="dd-detail-title">{spec.title}</h3>}
          <button className="dd-icon-btn" onClick={onClose} title="Close">×</button>
        </div>
        <div className="dd-detail-meta">
          <span className={`dd-cat-pill cat-${spec.category}`}>{CATEGORY_LABEL[spec.category]}</span>
          <span className="dd-meta-id">{spec.id}</span>
          {editing ? (
            <input
              className="dd-detail-system-input"
              placeholder="System name (e.g. SnakeGameSystem)"
              value={draftSystem}
              onChange={e => setDraftSystem(e.target.value)}
            />
          ) : (
            spec.systemName && <span className="dd-meta-system">⟶ {spec.systemName}</span>
          )}
          {spec.supersededBy && <span className="dd-meta-archived">archived</span>}
        </div>
      </div>

      <div className="dd-detail-body">
        {editing ? (
          <textarea
            className="dd-detail-body-input"
            value={draftBody}
            onChange={e => setDraftBody(e.target.value)}
            rows={Math.max(8, draftBody.split('\n').length + 2)}
          />
        ) : (
          <pre className="dd-detail-body-text">{spec.body}</pre>
        )}
      </div>

      <div className="dd-detail-section">
        <div className="dd-section-label">Bound code symbols</div>
        {spec.relatedCodeSymbols.length === 0
          ? <div className="dd-muted dd-section-empty">No bindings — change-impact audits will rely on semantic match only.</div>
          : <ul className="dd-bindings">
              {spec.relatedCodeSymbols.map((ref, i) => (
                <li key={`${ref.path}:${ref.symbol ?? ''}:${i}`}>
                  <code>{ref.symbol ? `${ref.path}:${ref.symbol}` : ref.path}</code>
                </li>
              ))}
            </ul>
        }
      </div>

      {linkedSpecs.length > 0 && (
        <div className="dd-detail-section">
          <div className="dd-section-label">Related specs</div>
          <ul className="dd-related">
            {linkedSpecs.map(s => (
              <li key={s.id}>
                <span className={`dd-cat-pill cat-${s.category}`}>{CATEGORY_LABEL[s.category]}</span>
                <span className="dd-related-title">{s.title}</span>
                <span className="dd-related-id">{s.id}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="dd-detail-section">
        <div className="dd-section-label">Timestamps</div>
        <div className="dd-meta-grid">
          <span>Created</span><span title={spec.createdAt}>{relativeTime(spec.createdAt)}</span>
          <span>Updated</span><span title={spec.updatedAt}>{relativeTime(spec.updatedAt)}</span>
        </div>
      </div>

      <div className="dd-detail-actions">
        {editing ? (
          <>
            <button className="dd-toolbar-btn primary" onClick={save}>Save</button>
            <button className="dd-toolbar-btn" onClick={cancel}>Cancel</button>
          </>
        ) : (
          <>
            <button className="dd-toolbar-btn primary" onClick={startEdit}>Edit</button>
            <button className="dd-toolbar-btn dd-danger" onClick={onDelete}>Delete</button>
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────

function filterAndSort(
  specs: DesignSpec[],
  opts: { category: CategoryFilter; search: string; showArchived: boolean; sort: SortState },
): DesignSpec[] {
  let out = specs.slice();
  if (!opts.showArchived) {
    out = out.filter(s => !s.supersededBy);
  }
  if (opts.category !== 'all') {
    out = out.filter(s => s.category === opts.category);
  }
  if (opts.search) {
    out = out.filter(s =>
      s.title.toLowerCase().includes(opts.search) ||
      s.body.toLowerCase().includes(opts.search) ||
      (s.systemName?.toLowerCase().includes(opts.search) ?? false) ||
      s.id.toLowerCase().includes(opts.search),
    );
  }

  out.sort((a, b) => compare(a, b, opts.sort));
  return out;
}

function compare(a: DesignSpec, b: DesignSpec, sort: SortState): number {
  const dir = sort.dir === 'asc' ? 1 : -1;
  switch (sort.column) {
    case 'title':    return dir * a.title.localeCompare(b.title);
    case 'category': return dir * a.category.localeCompare(b.category);
    case 'system':   return dir * (a.systemName ?? '').localeCompare(b.systemName ?? '');
    case 'bindings': return dir * (a.relatedCodeSymbols.length - b.relatedCodeSymbols.length);
    case 'updated':  return dir * a.updatedAt.localeCompare(b.updatedAt);
  }
}

function countByCategory(specs: DesignSpec[], includeArchived: boolean): Record<CategoryFilter, number> {
  const out: Record<CategoryFilter, number> = {
    all: 0,
    'overview': 0,
    'mechanic': 0,
    'system-contract': 0,
    'decision': 0,
    'open-question': 0,
  };
  for (const s of specs) {
    if (!includeArchived && s.supersededBy) continue;
    out.all += 1;
    out[s.category] += 1;
  }
  return out;
}

function relativeTime(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  if (Number.isNaN(ms)) return iso;
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}
